<script setup>
import { computed, onMounted, ref } from 'vue';
import dayjs from 'dayjs';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import WorkforceAPI from 'dashboard/api/workforce';
import WorkforceButton from '../components/WorkforceButton.vue';

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');

const loadingStatus = ref(false);
const loadingAttendance = ref(false);
const actionLoading = ref(false);

const selfStatus = ref(null);
const attendanceSessions = ref([]);
const lastUpdatedAt = ref(null);

const rangeFrom = computed(() =>
  dayjs().subtract(6, 'day').format('YYYY-MM-DD')
);
const rangeTo = computed(() => dayjs().format('YYYY-MM-DD'));

const hasOpenSession = computed(() => {
  return Boolean(
    selfStatus.value?.session && !selfStatus.value.session?.ended_at
  );
});

const inBreak = computed(() =>
  Boolean(selfStatus.value?.session?.active_break)
);
const scheduleSegments = computed(
  () => selfStatus.value?.schedule_segments || []
);
const pendingConversations = computed(
  () => selfStatus.value?.conversations || []
);
const nextSegment = computed(() => selfStatus.value?.next_segment || null);

const statusLabel = computed(() => {
  if (inBreak.value) return t('WORKFORCE.STATUS.ON_BREAK');
  if (hasOpenSession.value) return t('WORKFORCE.STATUS.ON_DUTY');
  return t('WORKFORCE.STATUS.OFF_DUTY');
});

const formatDateTime = value => {
  if (!value) return '--';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '--';
};

const formatTime = value => {
  if (!value) return '--';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('HH:mm') : '--';
};

const formatDuration = seconds => {
  const total = Number(seconds || 0);
  if (!total) return '0m';

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const waitingLabel = seconds => {
  const total = Number(seconds || 0);
  if (!total) return t('WORKFORCE.LIVE.COLUMNS.JUST_NOW');
  if (total < 60) return `${Math.max(total, 1)}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const residual = minutes % 60;
  return `${hours}h ${residual}m`;
};

const fetchSelfStatus = async () => {
  if (!currentUser.value?.id) return;

  loadingStatus.value = true;
  try {
    const response = await WorkforceAPI.getSelfStatus();
    selfStatus.value = response?.agent || null;
    lastUpdatedAt.value = new Date();
  } catch (error) {
    useAlert(t('WORKFORCE.MY_SHIFT.TOASTS.LOAD_STATUS_ERROR'));
  } finally {
    loadingStatus.value = false;
  }
};

const fetchAttendance = async () => {
  if (!currentUser.value?.id) return;

  loadingAttendance.value = true;
  try {
    const response = await WorkforceAPI.getSelfAttendance({
      from: rangeFrom.value,
      to: rangeTo.value,
    });
    attendanceSessions.value = response || [];
  } catch (error) {
    useAlert(t('WORKFORCE.MY_SHIFT.TOASTS.LOAD_ATTENDANCE_ERROR'));
  } finally {
    loadingAttendance.value = false;
  }
};

const refreshAll = async () => {
  await Promise.all([fetchSelfStatus(), fetchAttendance()]);
};

const resolveAttendanceErrorMessage = error => {
  const code = error?.response?.data?.error;
  if (!code) return null;

  const dictionary = {
    session_already_open: t('WORKFORCE.TOASTS.SESSION_ALREADY_OPEN'),
    no_open_session: t('WORKFORCE.TOASTS.NO_OPEN_SESSION'),
    break_already_active: t('WORKFORCE.TOASTS.BREAK_ALREADY_ACTIVE'),
    break_not_active: t('WORKFORCE.TOASTS.BREAK_NOT_ACTIVE'),
    forbidden: t('WORKFORCE.TOASTS.PERMISSION_ERROR'),
    workforce_management_disabled: t('WORKFORCE.TOASTS.PERMISSION_ERROR'),
  };

  return dictionary[code] || (typeof code === 'string' ? code : null);
};

const handleAttendanceAction = async action => {
  if (!currentUser.value?.id) return;

  actionLoading.value = true;
  const payload = {
    timestamp: new Date().toISOString(),
    source: 'manual',
  };

  try {
    switch (action) {
      case 'check_in':
        await WorkforceAPI.checkIn(payload);
        useAlert(t('WORKFORCE.TOASTS.CHECK_IN_SUCCESS'));
        break;
      case 'check_out':
        await WorkforceAPI.checkOut(payload);
        useAlert(t('WORKFORCE.TOASTS.CHECK_OUT_SUCCESS'));
        break;
      case 'start_break':
        await WorkforceAPI.startBreak(payload);
        useAlert(t('WORKFORCE.TOASTS.START_BREAK_SUCCESS'));
        break;
      case 'end_break':
        await WorkforceAPI.endBreak(payload);
        useAlert(t('WORKFORCE.TOASTS.END_BREAK_SUCCESS'));
        break;
      default:
        break;
    }

    await refreshAll();
  } catch (error) {
    const message =
      resolveAttendanceErrorMessage(error) ||
      t('WORKFORCE.TOASTS.ACTION_ERROR');
    useAlert(message);
  } finally {
    actionLoading.value = false;
  }
};

onMounted(() => {
  refreshAll();
});
</script>

<template>
  <section class="flex flex-col gap-5 px-8 py-5 lg:px-10">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('WORKFORCE.MY_SHIFT.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-10">
          {{ t('WORKFORCE.MY_SHIFT.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-n-slate-10">
          {{ t('WORKFORCE.MY_SHIFT.LAST_UPDATED') }}:
          {{ formatDateTime(lastUpdatedAt) }}
        </span>
        <WorkforceButton
          size="sm"
          variant="outline"
          color="onyx"
          :is-loading="loadingStatus || loadingAttendance"
          icon="i-lucide-refresh-cw"
          @click="refreshAll"
        >
          {{ t('WORKFORCE.ACTIONS.REFRESH') }}
        </WorkforceButton>
      </div>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-solid-2 p-5 shadow-sm">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p class="text-xs uppercase tracking-wide text-n-slate-9">
            {{ t('WORKFORCE.MY_SHIFT.CURRENT_STATUS') }}
          </p>
          <p class="mt-1 text-base font-semibold text-n-slate-12">
            {{ statusLabel }}
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <WorkforceButton
            v-if="!hasOpenSession"
            size="sm"
            :is-loading="actionLoading"
            icon="i-lucide-play"
            @click="handleAttendanceAction('check_in')"
          >
            {{ t('WORKFORCE.ACTIONS.START_SHIFT') }}
          </WorkforceButton>
          <template v-else>
            <WorkforceButton
              v-if="!inBreak"
              size="sm"
              variant="outline"
              color="onyx"
              :is-loading="actionLoading"
              icon="i-lucide-mug-hot"
              @click="handleAttendanceAction('start_break')"
            >
              {{ t('WORKFORCE.ACTIONS.START_BREAK') }}
            </WorkforceButton>
            <WorkforceButton
              v-else
              size="sm"
              variant="outline"
              color="onyx"
              :is-loading="actionLoading"
              icon="i-lucide-coffee"
              @click="handleAttendanceAction('end_break')"
            >
              {{ t('WORKFORCE.ACTIONS.END_BREAK') }}
            </WorkforceButton>
            <WorkforceButton
              size="sm"
              :is-loading="actionLoading"
              icon="i-lucide-stop-circle"
              @click="handleAttendanceAction('check_out')"
            >
              {{ t('WORKFORCE.ACTIONS.END_SHIFT') }}
            </WorkforceButton>
          </template>
        </div>
      </div>
    </div>

    <div class="grid gap-5 lg:grid-cols-2">
      <div class="rounded-xl border border-n-weak bg-n-solid-2 p-5 shadow-sm">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('WORKFORCE.MY_SHIFT.SCHEDULE_TITLE') }}
        </h3>
        <p v-if="nextSegment" class="mt-2 text-xs text-n-slate-10">
          {{ t('WORKFORCE.MY_SHIFT.NEXT_SHIFT') }}:
          {{ formatDateTime(nextSegment.start_time) }} -
          {{ formatDateTime(nextSegment.end_time) }}
        </p>
        <p v-else class="mt-2 text-xs text-n-slate-10">
          {{ t('WORKFORCE.STATUS.NO_SCHEDULE') }}
        </p>

        <ul v-if="scheduleSegments.length" class="mt-4 space-y-2">
          <li
            v-for="(segment, index) in scheduleSegments"
            :key="`segment-${index}`"
            class="flex items-center justify-between rounded-lg border border-n-weak bg-n-solid-3 px-3 py-2 text-sm"
          >
            <span class="font-medium text-n-slate-12">
              {{ formatTime(segment.start_time) }} -
              {{ formatTime(segment.end_time) }}
            </span>
            <span class="text-xs text-n-slate-9">
              {{ segment.timezone || 'UTC' }}
            </span>
          </li>
        </ul>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-solid-2 p-5 shadow-sm">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('WORKFORCE.MY_SHIFT.PENDING_TITLE') }}
        </h3>
        <p
          v-if="!pendingConversations.length"
          class="mt-3 text-sm text-n-slate-10"
        >
          {{ t('WORKFORCE.MY_SHIFT.PENDING_EMPTY') }}
        </p>
        <ul v-else class="mt-3 space-y-2">
          <li
            v-for="conversation in pendingConversations.slice(0, 8)"
            :key="conversation.id"
            class="rounded-lg border border-n-weak bg-n-solid-3 px-3 py-2"
          >
            <div class="flex items-center justify-between gap-2">
              <p class="text-sm font-semibold text-n-slate-12">
                #{{ conversation.display_id }}
              </p>
              <p class="text-xs text-n-slate-9">
                {{ waitingLabel(conversation.waiting_seconds) }}
              </p>
            </div>
            <p class="mt-1 text-xs text-n-slate-10">
              {{
                conversation.customer_name ||
                t('WORKFORCE.LABELS.UNKNOWN_AGENT')
              }}
              · {{ conversation.inbox_name || '--' }}
            </p>
          </li>
        </ul>
      </div>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
      <div
        class="flex items-center justify-between border-b border-n-weak px-5 py-3"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('WORKFORCE.MY_SHIFT.HISTORY_TITLE') }}
        </h3>
        <span class="text-xs text-n-slate-10">
          {{ t('WORKFORCE.MY_SHIFT.HISTORY_SUBTITLE') }}
        </span>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-n-weak text-sm">
          <thead
            class="bg-n-solid-2 text-left text-xs uppercase tracking-wide text-n-slate-10"
          >
            <tr>
              <th class="px-5 py-3">
                {{ t('WORKFORCE.LIVE.COLUMNS.SESSION') }}
              </th>
              <th class="px-5 py-3">
                {{ t('WORKFORCE.LIVE.COLUMNS.STARTED_AT') }}
              </th>
              <th class="px-5 py-3">
                {{ t('WORKFORCE.ATTENDANCE.TABLE.BREAK') }}
              </th>
              <th class="px-5 py-3">{{ t('WORKFORCE.TABLE.STATUS') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak/70 bg-n-solid-3 text-n-slate-12">
            <tr v-if="loadingAttendance">
              <td
                colspan="4"
                class="px-5 py-6 text-center text-sm text-n-slate-10"
              >
                {{ t('WORKFORCE.ATTENDANCE.TABLE.LOADING') }}
              </td>
            </tr>
            <tr v-else-if="!attendanceSessions.length">
              <td
                colspan="4"
                class="px-5 py-6 text-center text-sm text-n-slate-10"
              >
                {{ t('WORKFORCE.MY_SHIFT.HISTORY_EMPTY') }}
              </td>
            </tr>
            <tr
              v-for="session in attendanceSessions.slice(0, 10)"
              :key="session.id"
            >
              <td class="px-5 py-3 text-n-slate-10">#{{ session.id }}</td>
              <td class="px-5 py-3">
                {{ formatDateTime(session.started_at) }}
              </td>
              <td class="px-5 py-3">
                {{ formatDuration(session.total_break_seconds) }}
              </td>
              <td class="px-5 py-3">
                <span
                  class="inline-flex rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="
                    session.status === 'open'
                      ? 'bg-teal-3/40 text-teal-11'
                      : 'bg-n-weak/30 text-n-slate-10'
                  "
                >
                  {{ session.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>
